import { Route, Routes, Navigate } from 'react-router-dom';

import { CreateVisitPageV2 } from 'pages/visits-page-v2/create-visit-page-v2';
import { EditVisitPageV2 } from 'pages/visits-page-v2/edit-visit-page-v2';
import { VisitDetailsPageV2 } from 'pages/visits-page-v2/visit-details-page-v2';
import { VisitsViewPageV2 } from 'pages/visits-page-v2/visits-view-page-v2';
import { VisitConfirmationPageV2 } from 'pages/visits-page-v2/visit-confirmation-page-v2';
import { VisitDeletePageV2 } from 'pages/visits-page-v2/visit-delete-page-v2';

/** The V1 list paths, kept resolving to the V2 list rather than being read as visit ids. */
const LEGACY_LIST_PATHS = ['all', 'upcoming', 'past', 'new'];

/**
 * Routes component for V2 visitor management UI
 * Simplified route structure for V2 implementation
 */
export const VisitsV2Routes = (): JSX.Element => {
  return (
    <Routes>
      {/*
        The list is the page; every one of these is a modal over it, filling its outlet. A pathless
        layout route lets `visits/:visitUuid` share that list without inheriting `view` into its URL —
        without it the detail route renders a modal floating over nothing.
      */}
      <Route element={<VisitsViewPageV2 />}>
        {/* View visits form */}
        <Route path="view">
          {/* Confirmation modal route nested under view */}
          <Route path=":visitId/confirm" element={<VisitConfirmationPageV2 />} />
          {/* Delete modal route nested under view */}
          <Route path=":visitId/delete" element={<VisitDeletePageV2 />} />
        </Route>
        {/*
          A visit in any state, by uuid alone — what a deeplink from activity center should land on.
          Static siblings (`view`, `create`, `edit`) still win: react-router ranks literal segments above
          dynamic ones, so this cannot swallow them regardless of order.
        */}
        <Route path=":visitUuid" element={<VisitDetailsPageV2 />} />
      </Route>
      {/*
        V1's single-segment paths. V2 has no tabs, so these used to fall through to the catch-all and
        land on the list. `:visitUuid` would otherwise swallow them and go looking for a visit called
        "all" — react-router ranks a literal segment above a dynamic one, so naming them restores it.
      */}
      {LEGACY_LIST_PATHS.map((path) => (
        <Route key={path} path={path} element={<Navigate to="../view" replace />} />
      ))}
      {/* Create visit form */}
      <Route path="create/*" element={<CreateVisitPageV2 />} />
      <Route path="edit/:visitId/*" element={<EditVisitPageV2 />} />
      {/* Dynamic routes - edit route must come before details route */}
      {/* Use /* to allow nested routes like /edit/visitor, /edit/date */}
      <Route path=":visitUiType/:visitId/edit/*" element={<EditVisitPageV2 />} />
      {/* Catch-all - List visits */}
      <Route path="*" element={<Navigate to="view" replace />} />
    </Routes>
  );
};
